// ============================================================
// CHARACTER FACTORIES - Pure functions for creating Character
// entities and CombatInstance objects with sensible defaults.
// ============================================================
import type {
  Character,
  CharacterWeapon,
  CombatInstance,
  CombatTab,
  Characteristics,
  CharacterSkills,
  ArmourSet,
} from './types';
import type { NarrativeEntry } from '../../types';
import deepClone from '../../utils/deepClone';
import { defaultArmourForBody } from '../bodyConfig';

/** Default characteristics: all 30, multiplier 1 */
function defaultCharacteristics(): Characteristics {
  const base = { value: 30, unnaturalMultiplier: 1 };
  return {
    ws: { ...base },
    bs: { ...base },
    str: { ...base },
    tgh: { ...base },
    agi: { ...base },
    int: { ...base },
    per: { ...base },
    wp: { ...base },
    fel: { ...base },
  };
}

/** Default skills: dodge/parry untrained (other skills absent = untrained) */
function defaultSkills(): CharacterSkills {
  return {
    dodge: { trained: false, plus10: false, plus20: false, mod: 0, bonus: 0 },
    parry: { trained: false, plus10: false, plus20: false, mod: 0, bonus: 0 },
  };
}

/** Default armour set: no armour (humanoid locations from body config) */
function defaultArmour(): ArmourSet {
  return {
    name: 'None',
    locations: defaultArmourForBody('humanoid'),
  };
}

/**
 * Create a new empty Character template with sensible defaults.
 * All characteristics at 30, no talents/traits/weapons.
 */
export function emptyCharacter(id?: string): Character {
  const now = Date.now();
  return {
    id: id || 'char-' + now,
    name: 'New Character',
    characteristics: defaultCharacteristics(),
    talents: [],
    traits: [],
    weapons: [],
    righteousFury: true, // profile-wide default; per-weapon overrides may disable it
    skills: defaultSkills(),
    armour: defaultArmour(),
    forceField: { active: false, protectionRating: 0, overloadThreshold: 0 },
    size: 'Average',
    cover: { level: 'none', ap: 0 },
    horde: { isHorde: false, magnitude: 30, maxMagnitude: 30 },
    psyRating: 0,
    fearRating: 0,
    xenos: false,
    wounds: { current: 15, max: 15 },
    fatigue: 0,
    movement: { half: 3, full: 6, charge: 9, run: 18 },
    tags: [],
    notes: '',
    codexQuote: '',
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Weapon ids must be unique within a sheet: the ammo ledger, macros and
 * the armoury rack all key off them. A bare Date.now() collides when two
 * weapons are minted in the same millisecond - double-clicking Add, or
 * duplicating - so a counter rides along (see roster/factories nextId).
 */
let weaponIdCounter = 0;
function nextWeaponId(): string {
  weaponIdCounter += 1;
  return `wpn-${Date.now().toString(36)}-${weaponIdCounter.toString(36)}`;
}

/**
 * Create a new empty CharacterWeapon with sensible defaults.
 */
export function emptyWeapon(id?: string): CharacterWeapon {
  return {
    id: id || nextWeaponId(),
    name: 'New Weapon',
    type: 'ranged',
    damageExpression: '1d10+5',
    damageType: 'X',
    penetration: 4,
    range: 100,
    rateOfFire: { single: true, semi: 0, full: 0 },
    specialRules: [],
    psyRating: 0,
    ammo: { current: 28, max: 28, reloadAction: 'Full' },
    attackMod: 0,
    coneLength: 0,
    righteousFury: null, // inherit the character's global RF default
  };
}

/**
 * The weapon list with a copy of `id` inserted directly below it.
 *
 * The copy takes a fresh id - two weapons sharing one would share one
 * ammo ledger - and a " (Copy)" name, which also keeps SlotEditor's
 * lookup-by-name unambiguous. Unknown id returns the array unchanged.
 */
export function duplicateWeapon(weapons: CharacterWeapon[], id: string): CharacterWeapon[] {
  const idx = weapons.findIndex((w) => w.id === id);
  if (idx === -1) return weapons;
  const copy = { ...deepClone(weapons[idx]), id: nextWeaponId(), name: `${weapons[idx].name} (Copy)` };
  return [...weapons.slice(0, idx + 1), copy, ...weapons.slice(idx + 1)];
}

/**
 * The weapon list with the entry at `from` re-inserted at `to` - insert
 * semantics, like the armoury's moveSlot, so dragging over a card lands
 * beside it rather than swapping with it. Out-of-range or same-place
 * moves return the SAME array, so callers can skip a no-op write.
 */
export function moveWeapon(weapons: CharacterWeapon[], from: number, to: number): CharacterWeapon[] {
  if (from === to || !isIndexIn(weapons, from) || !isIndexIn(weapons, to)) return weapons;
  const next = [...weapons];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function isIndexIn(list: readonly unknown[], i: number): boolean {
  // Negated rather than `i >= 0 && i < length` so a NaN index is not refused, as it never was.
  return !(i < 0 || i >= list.length);
}

/**
 * Create a CombatInstance from a Character - the mutable combat state.
 * Deep clones the character to snapshot it at load time.
 */
export function emptyCombatInstance(character: Character): CombatInstance {
  const charSnapshot = deepClone(character);

  // Build per-weapon ammo map (tolerate partial characters - Firestore
  // snapshots and test fixtures may omit arrays)
  const weaponAmmo: Record<string, { current: number; max: number; reloadAction: string }> = {};
  for (const w of charSnapshot.weapons ?? []) {
    weaponAmmo[w.id] = { ...w.ammo };
  }

  return {
    characterId: charSnapshot.id,
    character: charSnapshot,
    wounds: { ...(charSnapshot.wounds ?? { current: 15, max: 15 }) },
    conditions: [],
    criticalDamage: [],
    armour: { ...(charSnapshot.armour?.locations ?? {}) },
    forceField: { ...(charSnapshot.forceField ?? { active: false, protectionRating: 0, overloadThreshold: 0 }) },
    horde: { ...(charSnapshot.horde ?? { isHorde: false, magnitude: 30, maxMagnitude: 30 }) },
    weaponAmmo,
    roundCountdown: false,
  };
}

/**
 * Create a CombatTab from a Character - a target tab with combat instance.
 */
export function createCombatTab(character: Character, id?: string): CombatTab {
  return {
    id: id || Date.now().toString(),
    label: character.name,
    instance: emptyCombatInstance(character),
    narrativeLog: [] as NarrativeEntry[],
  };
}
